import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { questionApi } from '../../services/adminApi';
import '../../styles/AdminPages.css';

const QuestionEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [statement, setStatement] = useState('');
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    if (!isNew) {
      loadQuestion();
    }
  }, [id]);

  const loadQuestion = async () => {
    try {
      setLoading(true);
      const response = await questionApi.getAll();
      const questions = response.data?.[0] || response.data || [];
      const question = questions.find(q => String(q.id) === String(id));
      if (!question) {
        setError('Question not found');
        return;
      }
      setStatement(question.statement || '');
      setAnswers(question.answers || []);
    } catch (err) {
      setError('Failed to load question');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnswer = () => {
    setAnswers([...answers, { 
      id: null, 
      answerTitle: '', 
      description: '',
      _tempId: Date.now()
    }]);
  };

  const handleRemoveAnswer = (index) => {
    setAnswers(answers.filter((_, i) => i !== index));
  };

  const handleAnswerChange = (index, field, value) => {
    const updated = [...answers];
    updated[index] = { ...updated[index], [field]: value };
    setAnswers(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!statement.trim()) {
      setError('Question statement is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        statement,
        answers: answers.map(a => ({
          id: a.id,
          answerTitle: a.answerTitle,
          description: a.description
        }))
      };

      if (isNew) {
        await questionApi.create(payload);
      } else {
        await questionApi.update(id, payload);
      }

      navigate('/admin/question');
    } catch (err) {
      setError(err.message || 'Failed to save question');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page" style={{ overflow: 'visible', height: 'auto', minHeight: 'auto' }}>
        <div className="admin-page-header">
          <div>
            <h1>{isNew ? 'Add New Question' : 'Edit Question'}</h1>
            <p>Manage question and its answers</p>
          </div>
          <button 
            className="admin-btn secondary"
            onClick={() => navigate('/admin/question')}
          >
            <i className="fas fa-arrow-left"></i> Back to Questions
          </button>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '20px', padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', color: '#c00' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="admin-table-card" style={{ padding: '24px', marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px', color: '#333' }}>Question Statement</h3>
            <textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              placeholder="Enter the question statement..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
              required
            />
          </div>

          <div className="admin-table-card" style={{ padding: '24px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ color: '#333', margin: 0 }}>Answers ({answers.length})</h3>
              <button
                type="button"
                onClick={handleAddAnswer}
                className="admin-btn primary"
              >
                <i className="fas fa-plus"></i> Add Answer
              </button>
            </div>

            {answers.length === 0 ? (
              <p style={{ color: '#666', fontStyle: 'italic' }}>No answers yet. Click "Add Answer" to create one.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {answers.map((answer, index) => (
                  <div 
                    key={answer.id || answer._tempId} 
                    style={{
                      padding: '16px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      background: '#fafafa'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontWeight: '600', color: '#555' }}>Answer {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAnswer(index)}
                        style={{
                          background: '#ff4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        <i className="fas fa-trash"></i> Remove
                      </button>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#555' }}>
                        Answer Title
                      </label>
                      <input
                        type="text"
                        value={answer.answerTitle || ''}
                        onChange={(e) => handleAnswerChange(index, 'answerTitle', e.target.value)}
                        placeholder="Enter answer title..."
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#555' }}>
                        Description
                      </label>
                      <textarea
                        value={answer.description || ''}
                        onChange={(e) => handleAnswerChange(index, 'description', e.target.value)}
                        placeholder="Enter answer description (optional)..."
                        style={{
                          width: '100%',
                          minHeight: '60px',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', paddingBottom: '40px' }}>
            <button
              type="submit"
              className="admin-btn primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : (isNew ? 'Create Question' : 'Save Changes')}
            </button>
            <button
              type="button"
              className="admin-btn secondary"
              onClick={() => navigate('/admin/question')}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
  );
};

export default QuestionEditPage;
